import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#fixMidParagraphUnboxImage();
    this.#collapseArticleFaq();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Move mid-paragraph unboxing photo after the full first paragraph
   * (image was pasted between "legs a" / "nd" in article body HTML).
   */
  #fixMidParagraphUnboxImage() {
    const img = this.querySelector('img[src*="vislund-unboxing-hardware-1"]');
    if (!img) return;

    const p = img.closest('p');
    if (!p || !p.parentNode) return;
    if (p.classList.contains('blog-post-figure')) return;

    let before = '';
    let after = '';
    let found = false;
    const nodes = p.childNodes;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (n === img) {
        found = true;
        continue;
      }
      const t = n.textContent || '';
      if (!found) before += t;
      else after += t;
    }

    // Only rewrite when the image sits mid-sentence (text on both sides).
    if (!before.trim() || !after.trim()) return;

    const newP = document.createElement('p');
    newP.textContent = (p.textContent || '').replace(/\s+/g, ' ').trim();

    const figure = document.createElement('p');
    figure.className = 'blog-post-figure';
    const moved = img.cloneNode(true);
    moved.setAttribute(
      'alt',
      'Vislund TV stand parts laid out on a coffee table before assembly'
    );
    moved.setAttribute('loading', 'lazy');
    figure.appendChild(moved);

    p.parentNode.insertBefore(newP, p);
    p.parentNode.insertBefore(figure, p);
    p.parentNode.removeChild(p);
  }

  /**
   * Convert Journal "Common questions" H3/P pairs into collapsible details.
   * Server-side Liquid does this too; this is a client fallback.
   */
  #collapseArticleFaq() {
    if (this.querySelector('[data-vislund-article-faq]')) return;

    const headings = this.querySelectorAll('h2');
    let faqHeading = null;
    for (const h of headings) {
      const t = (h.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (t === 'common questions' || t === 'faq' || t === 'frequently asked questions') {
        faqHeading = h;
        break;
      }
    }
    if (!faqHeading) return;

    const isStopNode = (el) => {
      if (!el) return true;
      if (el.tagName === 'H2' || el.tagName === 'H3') return true;
      if (el.classList && el.classList.contains('vislund-further-reading')) return true;
      return false;
    };

    const pairs = [];
    let node = faqHeading.nextElementSibling;
    while (node) {
      const next = node.nextElementSibling;
      if (node.tagName === 'H3') {
        const answerNodes = [];
        let cursor = next;
        while (cursor && !isStopNode(cursor)) {
          answerNodes.push(cursor);
          cursor = cursor.nextElementSibling;
        }
        if (answerNodes.length) {
          pairs.push({ question: node, answers: answerNodes });
          node = cursor;
          continue;
        }
      } else if (node.tagName === 'H2' || (node.classList && node.classList.contains('vislund-further-reading'))) {
        break;
      }
      node = next;
    }

    if (!pairs.length) return;

    const wrap = document.createElement('div');
    wrap.className = 'vislund-article-faq';
    wrap.setAttribute('data-vislund-article-faq', '');

    for (const pair of pairs) {
      const details = document.createElement('details');
      details.className = 'vislund-article-faq__item';

      const summary = document.createElement('summary');
      summary.className = 'vislund-article-faq__summary';

      const q = document.createElement('span');
      q.className = 'vislund-article-faq__question';
      q.textContent = (pair.question.textContent || '').replace(/\s+/g, ' ').trim();

      const chevron = document.createElement('span');
      chevron.className = 'vislund-article-faq__chevron';
      chevron.setAttribute('aria-hidden', 'true');

      summary.appendChild(q);
      summary.appendChild(chevron);

      const answer = document.createElement('div');
      answer.className = 'vislund-article-faq__answer';
      for (const a of pair.answers) {
        answer.appendChild(a);
      }

      details.appendChild(summary);
      details.appendChild(answer);
      wrap.appendChild(details);
      pair.question.remove();
    }

    faqHeading.classList.add('vislund-article-faq__heading');
    faqHeading.parentNode.insertBefore(wrap, faqHeading.nextSibling);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}


